/**
 * GIS (Google Identity Services) ベースの Google サインインボタン。
 *
 * 設計書: docs/architecture/partner-aware-google-oauth-design.md §4.2 / §4.3
 *
 * 動作:
 *  - マウント時に signInWithGoogle() を起動
 *    - POST /api/auth/google/nonce で nonce と client_id を取得
 *    - GIS スクリプトを動的読込
 *    - google.accounts.id.initialize() + prompt() で One-Tap を表示
 *    - GIS 標準ボタン (renderButton) をパーソナライズ表示込みで描画
 *  - 描画されたボタンは GIS 純正のスタイルそのまま (ブランドガイドライン準拠)
 *  - :root の color-scheme: dark の影響で GIS の outline テーマが意図しない
 *    白背景になる現象を、container に局所 color-scheme: only dark を当てて回避
 *    (GoogleSignInButton.module.css 参照)
 *
 * React Strict Mode の二重実行に備えて initiated ref で副作用の重複を回避。
 */

import { useEffect, useRef, useState } from 'react';
import styles from './GoogleSignInButton.module.css';
import { signInWithGoogle, type GoogleSignInResult } from './googleSignIn';

type Props = {
  /** partner_code (brandConfig.id を渡す) */
  partnerCode: string;
  /** 招待リンク経由の場合のトークン */
  invitationToken?: string;
  /**
   * ボタン文言モード (GIS renderButton の text オプションに対応)。
   * - 'continue' (既定): 「Google で続ける」 (ログイン画面など)
   * - 'signup':   「Google で登録」 (アカウント作成画面)
   * - 'signin':   「Google でログイン」
   */
  mode?: 'continue' | 'signup' | 'signin';
  /**
   * ログイン画面からの認証か、新規登録画面からの認証かの申告。
   * signInWithGoogle() へそのまま透過する。計画書 §4.6。
   */
  intent?: 'login' | 'signup';
  /**
   * 自身の initError を `<div>` で表示するか (既定 true = 現行互換)。
   * false を渡した場合、onError は従来どおり呼ばれるが自己表示は行わない
   * ―― エラー表示の責務を呼び出し元 (AuthGate / SignupSelectPage 等) に
   * 一本化し、親側の表示と二重にならないようにするため。
   */
  renderError?: boolean;
  onSuccess: (result: GoogleSignInResult) => void;
  onError?: (error: Error) => void;
};

const MODE_TO_TEXT = {
  continue: 'continue_with',
  signup: 'signup_with',
  signin: 'signin_with',
} as const;

export function GoogleSignInButton({
  partnerCode,
  invitationToken,
  mode = 'continue',
  intent,
  renderError = true,
  onSuccess,
  onError,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const initiated = useRef(false);
  const [initError, setInitError] = useState<string | null>(null);

  useEffect(() => {
    if (initiated.current) return;
    const container = containerRef.current;
    if (!container) return;
    initiated.current = true;

    signInWithGoogle({
      partnerCode,
      invitationToken,
      fallbackButtonContainer: container,
      buttonText: MODE_TO_TEXT[mode],
      intent,
    })
      .then(onSuccess)
      .catch((err: Error) => {
        setInitError(err.message);
        onError?.(err);
      });
  }, [partnerCode, invitationToken, mode, intent, onSuccess, onError]);

  return (
    <div>
      <div ref={containerRef} className={styles.container} />
      {renderError && initError && <div className={styles.errorMessage}>{initError}</div>}
    </div>
  );
}
